'use client';

import { CircularProgress, Divider, Stack, Typography } from '@mui/material';
import type { JobTemplate } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { OptionMenu } from '@/components/option-menu/option-menu';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { dayjs } from '@/utils/dayjs';

import { DeleteJobTemplateDialog } from './deleteJobTemplateDialog';
import { EditJobTemplateDialog } from './editJobTemplateDialog';
import { StartJobFromTemplateDialog } from './startJobFromTemplateDialog';

interface JobTemplatePanelProps {
  id: string;
  workspaceId: string;
  onClose: () => void;
  onRefresh: () => void;
}

export const JobTemplatePanel = ({
  id,
  workspaceId,
  onClose,
  onRefresh,
}: JobTemplatePanelProps) => {
  const dal = useDAL();
  const { isAuthorized } = useRoles();
  const [startJobDialogOpen, setStartJobDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: templatesData, isLoading: isTemplatesLoading } =
    dal.lakehouse.useListJobTemplates(workspaceId);

  const template = templatesData?.jobTemplates?.find(
    (t: JobTemplate) => t.id === id
  );

  if (isTemplatesLoading || !template) {
    return (
      <PanelWrapper
        header={{
          title: 'Job Template Details',
          onClose,
        }}
      >
        <Stack alignItems='center' justifyContent='center' flex={1}>
          <CircularProgress />
        </Stack>
      </PanelWrapper>
    );
  }

  const canCreateJob = isAuthorized('create:spark_job');
  const canUpdateTemplate = isAuthorized('update:job_template');
  const canDeleteTemplate = isAuthorized('delete:job_template');

  return (
    <>
      <PanelWrapper
        header={{
          title: 'Job Template Details',
          onClose,
          actions: (
            <OptionMenu
              options={[
                {
                  id: 'start-job',
                  text: 'Start Job',
                  icon: 'material-symbols-play-arrow-rounded',
                  menuItemProps: {
                    disabled: !canCreateJob,
                    onClick: () => setStartJobDialogOpen(true),
                  },
                },
                {
                  id: 'edit',
                  text: 'Edit',
                  icon: 'material-symbols-edit-outline-rounded',
                  menuItemProps: {
                    disabled: !canUpdateTemplate,
                    onClick: () => setEditDialogOpen(true),
                  },
                },
                {
                  id: 'divider',
                  divider: true,
                },
                {
                  id: 'delete',
                  text: 'Delete',
                  icon: 'material-symbols-delete-outline-rounded',
                  menuItemProps: {
                    disabled: !canDeleteTemplate,
                    onClick: () => setDeleteDialogOpen(true),
                  },
                },
              ]}
            />
          ),
        }}
      >
        <Stack gap='24px' padding='24px 40px'>
          {/* Template Details */}
          <Stack gap='12px'>
            <DetailRow label='ID' value={template.id} monospace />
            <DetailRow label='Name' value={template.name} />
            <DetailRow
              label='Description'
              value={template.description ?? '-'}
            />
            <DetailRow
              label='Created'
              value={dayjs
                .utc(template.createdAt)
                .format('MMM D, YYYY HH:mm:ss')}
            />
            {template.jobTimeout && (
              <DetailRow
                label='Job Timeout'
                value={`${template.jobTimeout} seconds`}
              />
            )}
          </Stack>

          <Divider />

          {/* Spark Submit Parameters */}
          <Stack gap='12px'>
            <Typography variant='subtitle2' color='text.secondary'>
              Spark Submit Parameters
            </Typography>
            {template.sparkSubmitParams && (
              <>
                <DetailRow
                  label='Entry Point'
                  value={template.sparkSubmitParams.entryPoint}
                  monospace
                />
                {template.sparkSubmitParams.entryPointArguments &&
                  template.sparkSubmitParams.entryPointArguments.length > 0 && (
                    <DetailRow
                      label='Arguments'
                      value={template.sparkSubmitParams.entryPointArguments.join(
                        ' '
                      )}
                      monospace
                    />
                  )}
                {template.sparkSubmitParams.sparkSubmitParameters && (
                  <DetailRow
                    label='Spark Params'
                    value={template.sparkSubmitParams.sparkSubmitParameters}
                    monospace
                  />
                )}
              </>
            )}
          </Stack>

          {/* Retry Policy */}
          {template.retryPolicy && (
            <>
              <Divider />
              <Stack gap='12px'>
                <Typography variant='subtitle2' color='text.secondary'>
                  Retry Policy
                </Typography>
                <DetailRow
                  label='Max Attempts'
                  value={String(template.retryPolicy.maxAttempts ?? 0)}
                />
                {template.retryPolicy.maxFailedAttemptsPerHour && (
                  <DetailRow
                    label='Max Failed/Hour'
                    value={String(
                      template.retryPolicy.maxFailedAttemptsPerHour
                    )}
                  />
                )}
              </Stack>
            </>
          )}
        </Stack>
      </PanelWrapper>

      <StartJobFromTemplateDialog
        template={template}
        workspaceId={workspaceId}
        open={startJobDialogOpen}
        onClose={() => setStartJobDialogOpen(false)}
        onJobStarted={onRefresh}
      />

      <EditJobTemplateDialog
        template={template}
        workspaceId={workspaceId}
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onUpdated={onRefresh}
      />

      <DeleteJobTemplateDialog
        template={template}
        workspaceId={workspaceId}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={() => {
          onRefresh();
          onClose();
        }}
      />
    </>
  );
};

const DetailRow = ({
  label,
  value,
  monospace,
}: {
  label: string;
  value?: React.ReactNode;
  monospace?: boolean;
}) => {
  return (
    <Stack direction='row' alignItems='flex-start' gap='16px'>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ minWidth: '120px' }}
      >
        {label}
      </Typography>
      {typeof value === 'string' ? (
        <Typography
          variant='body2'
          sx={{
            wordBreak: 'break-word',
            flex: 1,
            ...(monospace && { fontFamily: 'monospace', fontSize: '0.85rem' }),
          }}
        >
          {value || '-'}
        </Typography>
      ) : (
        (value ?? (
          <Typography variant='body2' sx={{ flex: 1 }}>
            -
          </Typography>
        ))
      )}
    </Stack>
  );
};
